import { supabase } from '../supabaseClient';
import type { ProgramContentSlideOwnerRef } from '../types/programContentSlideCanvas';
import { parseMemoFieldStorageDeep } from '../utils/programCellDisplayText';
import { textForStorage } from '../utils/programContentNullNormalize';
import {
  isPersistableUuid,
  pickProgramContentSlideOwnerWithDbPresence,
  resolvePresentationRefIdFromLearningText,
} from '../utils/programContentSlideOwner';
import {
  parseSlideLayoutJson,
  slideLayoutHasMeaningfulCanvasContent,
  type ProgramSlideLayoutV1,
} from '../types/programContentSlideCanvas';
import { invalidateProgramPresentationListStatsCache } from '../utils/programPresentationListStatsCache';
import {
  invalidateAllPresentationAuditQueriesForWorkspace,
  invalidatePresentationOwnerStatsQueries,
  removePresentationOwnerStatsCacheKeys,
} from '../query/cacheUpdates';
import { finalizePendingSlideImagesInLayout } from '../utils/slideImagePendingUpload';
import {
  learningContentFieldsToRpcParams,
  mergeLearningContentPatchWithCurrentDbBaseline,
  mergeLearningTextPatchWithCurrentDbBaseline,
} from '../utils/programLearningTextPatchMerge';
import { parseProgramLearningContentFieldsFromEditor } from '../utils/programLearningContentFields';
import { clearPresentationRefBindingAfterEmptySlideSave } from './programEmptyPresentationBindingService';
import type { ProgramContentSlidePayloadV1 } from '../utils/programContentSlideStorage';

/** 슬라이드 일괄 저장 RPC와 함께 갱신할 학습내용(진도 셀·고정·편제 행) */
export type ProgramSlideLearningTextPatch = {
  kind: 'program_cell' | 'fixed_row' | 'scoped_row';
  id: string;
  learningText: string;
};

const PRESENCE_IN_CHUNK = 80;
export const PROGRAM_CONTENT_SLIDE_PRESENCE_CACHE_PREFIX = 'cp:program-content-slide-presence:v4:';
export const PROGRAM_CONTENT_SLIDE_DRAFT_PREFIX = 'cp:program-content-slide-draft:v2:';
const CLONE_INTENT_PREFIX = 'cp:program-content-slide-clone-intent:v2:';
const PROGRAM_CONTENT_SLIDE_LAYOUT_CACHE_PREFIX = 'cp:program-content-slide-layout-cache:v1:';
export const PROGRAM_CONTENT_SLIDE_DRAFTS_CHANGED_EVENT = 'cp:program-content-slide-drafts-changed';
const presenceMemoryCache = new Map<string, Record<string, 0 | 1>>();
const slidePageSaveLocks = new Map<string, Promise<void>>();
const slideBundleSaveLocks = new Map<string, Promise<Record<number, ProgramSlideLayoutV1> | void>>();

interface ProgramContentSlideDraftRecordV1 {
  v: 1;
  workspaceId: string;
  scopeId: string;
  owner: ProgramContentSlideOwnerRef;
  layoutsByPageIndex: Record<string, ProgramSlideLayoutV1>;
  deleteAll?: boolean;
  learningPatch?: ProgramSlideLearningTextPatch | null;
  /** 연간진도표·프로그램 표 인디케이터용 — 활동지 draftRowKey와 동일 역할 */
  annualCellKey?: string;
  emptySlideClearMeta?: {
    presentationRefId?: string;
    sourceOwner?: ProgramContentSlideOwnerRef | null;
    bindingLearningText?: string | null;
    academicYear: number;
    termNumber: 1 | 2;
    annualCellKey?: string;
    contextGradeId?: string;
    createPresentationRefOnSave?: ProgramContentSlidePayloadV1['createPresentationRefOnSave'];
  } | null;
  updatedAt: number;
}

interface ProgramContentSlideCloneIntentV1 {
  v: 1;
  workspaceId: string;
  scopeId: string;
  from: ProgramContentSlideOwnerRef;
  to: ProgramContentSlideOwnerRef;
  updatedAt: number;
}

function draftStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function draftKey(workspaceId: string, owner: ProgramContentSlideOwnerRef): string {
  return `${PROGRAM_CONTENT_SLIDE_DRAFT_PREFIX}${workspaceId.trim()}:${owner.kind}:${owner.id.trim()}`;
}

function cloneIntentKey(workspaceId: string, owner: ProgramContentSlideOwnerRef): string {
  return `${CLONE_INTENT_PREFIX}${workspaceId.trim()}:${owner.kind}:${owner.id.trim()}`;
}

function readDraftRecord(workspaceId: string, owner: ProgramContentSlideOwnerRef): ProgramContentSlideDraftRecordV1 | null {
  const sid = workspaceId.trim();
  const oid = owner?.id?.trim();
  if (!sid || !oid) return null;
  const storage = draftStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(draftKey(sid, owner));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ProgramContentSlideDraftRecordV1;
    if (!parsed || parsed.v !== 1) return null;
    if ((parsed.workspaceId ?? '').trim() !== sid) return null;
    if ((parsed.owner?.id ?? '').trim() !== oid || parsed.owner?.kind !== owner.kind) return null;
    const map =
      parsed.layoutsByPageIndex && typeof parsed.layoutsByPageIndex === 'object' ? parsed.layoutsByPageIndex : {};
    return { ...parsed, layoutsByPageIndex: map };
  } catch {
    return null;
  }
}

function writeDraftRecord(rec: ProgramContentSlideDraftRecordV1): void {
  const storage = draftStorage();
  if (!storage) return;
  try {
    storage.setItem(draftKey(rec.workspaceId, rec.owner), JSON.stringify(rec));
    window.dispatchEvent(new CustomEvent(PROGRAM_CONTENT_SLIDE_DRAFTS_CHANGED_EVENT));
  } catch {
    /* ignore */
  }
}

function removeDraftRecord(workspaceId: string, owner: ProgramContentSlideOwnerRef): void {
  const storage = draftStorage();
  if (!storage) return;
  try {
    storage.removeItem(draftKey(workspaceId, owner));
    window.dispatchEvent(new CustomEvent(PROGRAM_CONTENT_SLIDE_DRAFTS_CHANGED_EVENT));
  } catch {
    /* ignore */
  }
}

function readCloneIntent(workspaceId: string, owner: ProgramContentSlideOwnerRef): ProgramContentSlideCloneIntentV1 | null {
  const sid = workspaceId.trim();
  const oid = owner?.id?.trim();
  const storage = draftStorage();
  if (!sid || !oid || !storage) return null;
  try {
    const raw = storage.getItem(cloneIntentKey(sid, owner));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ProgramContentSlideCloneIntentV1;
    if (!parsed || parsed.v !== 1) return null;
    if ((parsed.workspaceId ?? '').trim() !== sid) return null;
    if (parsed.to?.kind !== owner.kind || (parsed.to?.id ?? '').trim() !== oid) return null;
    const fromId = (parsed.from?.id ?? '').trim();
    const toId = (parsed.to?.id ?? '').trim();
    if (!fromId || !toId) return null;
    return {
      ...parsed,
      workspaceId: sid,
      scopeId: (parsed.scopeId ?? '').trim(),
      from: { kind: parsed.from.kind, id: fromId },
      to: { kind: parsed.to.kind, id: toId },
    };
  } catch {
    return null;
  }
}

function writeCloneIntent(intent: ProgramContentSlideCloneIntentV1): void {
  const storage = draftStorage();
  if (!storage) return;
  try {
    storage.setItem(cloneIntentKey(intent.workspaceId, intent.to), JSON.stringify(intent));
  } catch {
    /* ignore */
  }
}

function removeCloneIntent(workspaceId: string, owner: ProgramContentSlideOwnerRef): void {
  const storage = draftStorage();
  if (!storage) return;
  try {
    storage.removeItem(cloneIntentKey(workspaceId, owner));
  } catch {
    /* ignore */
  }
}

function listDraftRecords(scopeIds?: readonly string[]): ProgramContentSlideDraftRecordV1[] {
  const storage = draftStorage();
  if (!storage) return [];
  const out: ProgramContentSlideDraftRecordV1[] = [];
  const scopeSet = scopeIds && scopeIds.length ? new Set(scopeIds.map((s) => s.trim()).filter(Boolean)) : null;
  try {
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (!key || !key.startsWith(PROGRAM_CONTENT_SLIDE_DRAFT_PREFIX)) continue;
      const raw = storage.getItem(key);
      if (!raw) continue;
      const rec = JSON.parse(raw) as ProgramContentSlideDraftRecordV1;
      if (!rec || rec.v !== 1) continue;
      const scopeId = (rec.scopeId ?? '').trim();
      if (scopeSet && !scopeSet.has(scopeId)) continue;
      out.push(rec);
    }
  } catch {
    return out;
  }
  return out;
}

export function queueProgramContentSlideDraftChange(params: {
  workspaceId: string;
  scopeId: string;
  owner: ProgramContentSlideOwnerRef;
  pageIndex: number;
  layout: ProgramSlideLayoutV1;
}): void {
  const sid = params.workspaceId.trim();
  const scopeId = params.scopeId.trim();
  const ownerId = params.owner?.id?.trim();
  if (!sid || !scopeId || !ownerId) return;
  const prev = readDraftRecord(sid, params.owner);
  const next: ProgramContentSlideDraftRecordV1 = {
    v: 1,
    workspaceId: sid,
    scopeId,
    owner: { kind: params.owner.kind, id: ownerId },
    layoutsByPageIndex: {
      ...(prev?.deleteAll ? {} : (prev?.layoutsByPageIndex ?? {})),
      [String(params.pageIndex)]: params.layout,
    },
    deleteAll: false,
    learningPatch: prev?.learningPatch ?? null,
    annualCellKey: prev?.annualCellKey,
    emptySlideClearMeta: null,
    updatedAt: Date.now(),
  };
  writeDraftRecord(next);
  setPresenceCached(sid, next.owner, true);
}

/** 슬라이드 탭 저장(L2): owner 단위 전체 레이아웃을 초안으로 교체 */
export function replaceProgramContentSlideDraft(params: {
  workspaceId: string;
  scopeId: string;
  owner: ProgramContentSlideOwnerRef;
  layoutsByPageIndex: Record<number, ProgramSlideLayoutV1>;
  deleteAll?: boolean;
  learningPatch?: ProgramSlideLearningTextPatch | null;
  annualCellKey?: string;
  emptySlideClearMeta?: ProgramContentSlideDraftRecordV1['emptySlideClearMeta'];
}): void {
  const sid = params.workspaceId.trim();
  const scopeId = params.scopeId.trim();
  const ownerId = params.owner?.id?.trim();
  if (!sid || !scopeId || !ownerId) return;
  const layouts: Record<string, ProgramSlideLayoutV1> = {};
  if (!params.deleteAll) {
    Object.entries(params.layoutsByPageIndex).forEach(([idxRaw, layout]) => {
      const idx = Number(idxRaw);
      if (!Number.isFinite(idx) || idx < 0) return;
      layouts[String(idx)] = layout;
    });
  }
  const annualCellKey =
    (params.annualCellKey ?? '').trim() ||
    (params.emptySlideClearMeta?.annualCellKey ?? '').trim() ||
    undefined;
  const next: ProgramContentSlideDraftRecordV1 = {
    v: 1,
    workspaceId: sid,
    scopeId,
    owner: { kind: params.owner.kind, id: ownerId },
    layoutsByPageIndex: layouts,
    deleteAll: !!params.deleteAll,
    learningPatch: params.learningPatch ?? null,
    ...(annualCellKey ? { annualCellKey } : {}),
    emptySlideClearMeta: params.emptySlideClearMeta ?? null,
    updatedAt: Date.now(),
  };
  writeDraftRecord(next);
  setPresenceCached(sid, next.owner, !params.deleteAll && Object.keys(layouts).length > 0);
}

export function queueProgramContentSlideDeleteDraft(params: {
  workspaceId: string;
  scopeId: string;
  owner: ProgramContentSlideOwnerRef;
}): void {
  const sid = params.workspaceId.trim();
  const scopeId = params.scopeId.trim();
  const ownerId = params.owner?.id?.trim();
  if (!sid || !scopeId || !ownerId) return;
  const next: ProgramContentSlideDraftRecordV1 = {
    v: 1,
    workspaceId: sid,
    scopeId,
    owner: { kind: params.owner.kind, id: ownerId },
    layoutsByPageIndex: {},
    deleteAll: true,
    updatedAt: Date.now(),
  };
  writeDraftRecord(next);
  setPresenceCached(sid, next.owner, false);
}

export function getProgramContentSlideDraftLayouts(
  workspaceId: string,
  owner: ProgramContentSlideOwnerRef
): Record<number, ProgramSlideLayoutV1> {
  const rec = readDraftRecord(workspaceId, owner);
  if (!rec || rec.deleteAll) return {};
  const out: Record<number, ProgramSlideLayoutV1> = {};
  Object.entries(rec.layoutsByPageIndex).forEach(([k, v]) => {
    const idx = Number(k);
    if (!Number.isFinite(idx) || idx < 0) return;
    out[idx] = parseSlideLayoutJson(v);
  });
  return out;
}

/**
 * 프레젠테이션 탭을 (다시) 열 때, 통합 저장 전 로컬 pending 초안(더티)을 로드하기 위한 조회.
 * owner → owner 후보 → annualCellKey 순으로 매칭하여 초안 레이아웃과 그 owner를 돌려준다.
 * (특히 첫 저장 때 새로 만든 presentation_ref 초안은 DB owner 후보로는 못 찾으므로 annualCellKey로 매칭)
 */
export function findPendingProgramContentSlideDraftForOpen(params: {
  workspaceId: string;
  owner?: ProgramContentSlideOwnerRef | null;
  /** 학습내용(learning_text)이 저장되는 행 — 동기화 트랙에서는 대표 행. 크로스-별칭 매칭 기준 */
  learningTextOwner?: ProgramContentSlideOwnerRef | null;
  ownerCandidates?: readonly ProgramContentSlideOwnerRef[];
  annualCellKey?: string;
  /**
   * 있으면 annualCellKey 매칭 시 같은 프로그램 스코프 초안만 허용.
   * (편제/프로젝트 표끼리 같은 일정 슬롯을 쓸 때 다른 표 초안이 섞이지 않게)
   */
  scopeId?: string;
}): { owner: ProgramContentSlideOwnerRef; layouts: Record<number, ProgramSlideLayoutV1> } | null {
  const sid = params.workspaceId.trim();
  if (!sid) return null;

  // 1) owner / owner 후보로 직접 초안 조회
  const tryOwners: ProgramContentSlideOwnerRef[] = [];
  if (params.owner?.id?.trim()) tryOwners.push(params.owner);
  for (const c of params.ownerCandidates ?? []) {
    if (c?.id?.trim()) tryOwners.push(c);
  }
  for (const o of tryOwners) {
    const rec = readDraftRecord(sid, o);
    if (rec && slideDraftRecordHasLayouts(rec)) {
      return { owner: rec.owner, layouts: getProgramContentSlideDraftLayouts(sid, rec.owner) };
    }
  }

  // 크로스-별칭(동기화 대표) 매칭용 키: owner 후보 + learning_text owner(대표 행)
  const keyOwners: ProgramContentSlideOwnerRef[] = [...tryOwners];
  if (params.learningTextOwner?.id?.trim()) keyOwners.push(params.learningTextOwner);
  const candidateKeys = new Set(
    keyOwners.map((o) => programContentSlidePresenceKey({ kind: o.kind, id: o.id.trim() }))
  );
  const ak = (params.annualCellKey ?? '').trim();
  const wantScope = (params.scopeId ?? '').trim();

  const ownerKeyMatches = (owner: ProgramContentSlideOwnerRef | null | undefined): boolean => {
    const id = owner?.id?.trim();
    if (!owner || !id) return false;
    return candidateKeys.has(programContentSlidePresenceKey({ kind: owner.kind, id }));
  };

  // 2) annualCellKey(같은 칸) 또는 대표/후보 owner(초안 owner·learningPatch·sourceOwner) 매칭.
  //    동기화 미러는 첫 저장 때 새 presentation_ref 초안이 생기고 learningPatch가 대표 scoped_row를
  //    가리키므로, 다른 별칭/대표에서 열어도 owner 경로로 같은 초안을 찾는다.
  //    annualCellKey만으로는 다른 프로그램 표(다른 scopeId) 초안을 끌어오지 않는다.
  const matched = listDraftRecords().find((rec) => {
    if ((rec.workspaceId ?? '').trim() !== sid) return false;
    if (!slideDraftRecordHasLayouts(rec)) return false;
    if (ak && slideDraftRecordAnnualKey(rec) === ak) {
      if (!wantScope || (rec.scopeId ?? '').trim() === wantScope) return true;
    }
    if (candidateKeys.size === 0) return false;
    if (ownerKeyMatches(rec.owner)) return true;
    if (ownerKeyMatches(rec.learningPatch)) return true;
    if (ownerKeyMatches(rec.emptySlideClearMeta?.sourceOwner)) return true;
    return false;
  });
  if (matched?.owner?.id?.trim()) {
    return { owner: matched.owner, layouts: getProgramContentSlideDraftLayouts(sid, matched.owner) };
  }
  return null;
}

export function hasPendingProgramContentSlideDraftsForScope(scopeId: string): boolean {
  const sid = scopeId.trim();
  if (!sid) return false;
  return listDraftRecords([sid]).length > 0;
}

export function hasAnyPendingProgramContentSlideDrafts(): boolean {
  return listDraftRecords().length > 0;
}

/** 프로그램 통합 저장(학년·학기 범위) — pending draft scopeId 수집용 */
export function collectProgramContentSlideDraftScopeIds(): string[] {
  const out = new Set<string>();
  listDraftRecords().forEach((rec) => {
    const scopeId = (rec.scopeId ?? '').trim();
    if (scopeId) out.add(scopeId);
  });
  return Array.from(out);
}

export function hasProgramContentSlideDraftForOwner(
  workspaceId: string,
  owner: ProgramContentSlideOwnerRef
): boolean {
  const rec = readDraftRecord(workspaceId, owner);
  if (!rec) return false;
  if (rec.deleteAll) return false;
  return Object.keys(rec.layoutsByPageIndex ?? {}).length > 0;
}

function slideDraftRecordHasLayouts(rec: ProgramContentSlideDraftRecordV1): boolean {
  if (rec.deleteAll) return false;
  return Object.keys(rec.layoutsByPageIndex ?? {}).length > 0;
}

function slideDraftRecordAnnualKey(rec: ProgramContentSlideDraftRecordV1): string {
  return (rec.annualCellKey ?? '').trim() || (rec.emptySlideClearMeta?.annualCellKey ?? '').trim();
}

/**
 * annualCellKey 별칭으로 로컬 슬라이드 초안 존재 여부.
 * `scopeId`/`allowedScopeIds`가 있으면 같은 프로그램 스코프(동기화 쌍 포함) 초안만 본다.
 * 연간진도표→프로그램 표는 owner/`learningPatch` 매칭으로 이어지고, scope 없이 호출하면 기존처럼 칸만 본다.
 */
export function hasProgramContentSlideDraftForAnnualKeys(
  workspaceId: string,
  annualKeys: readonly string[],
  options?: { scopeId?: string; allowedScopeIds?: readonly string[] }
): boolean {
  const sid = workspaceId.trim();
  if (!sid) return false;
  const keySet = new Set(annualKeys.map((k) => k.trim()).filter(Boolean));
  if (!keySet.size) return false;
  const allowed = normalizeSlideDraftAllowedScopeIds(options);
  return listDraftRecords().some((rec) => {
    if ((rec.workspaceId ?? '').trim() !== sid) return false;
    if (!slideDraftRecordHasLayouts(rec)) return false;
    if (!slideDraftRecordMatchesAllowedScope(rec, allowed)) return false;
    const ak = slideDraftRecordAnnualKey(rec);
    return !!ak && keySet.has(ak);
  });
}

function normalizeSlideDraftAllowedScopeIds(options?: {
  scopeId?: string;
  allowedScopeIds?: readonly string[];
}): Set<string> {
  const raw =
    options?.allowedScopeIds && options.allowedScopeIds.length
      ? options.allowedScopeIds
      : options?.scopeId
        ? [options.scopeId]
        : [];
  return new Set(raw.map((s) => s.trim()).filter(Boolean));
}

function slideDraftRecordMatchesAllowedScope(
  rec: ProgramContentSlideDraftRecordV1,
  allowed: ReadonlySet<string>
): boolean {
  if (!allowed.size) return true;
  const recScope = (rec.scopeId ?? '').trim();
  /** scope 없는 레거시 초안은 다른 표 잔상으로 쓰지 않는다 */
  if (!recScope) return false;
  return allowed.has(recScope);
}

/**
 * 행 owner 후보와 draft.owner / learningPatch가 일치하는 로컬 초안(presentation_ref 이전·이후 모두).
 * `scopeId`/`allowedScopeIds`가 있으면 해당 프로그램 스코프(동기화 대표·미러 포함) 초안만 본다.
 */
export function hasProgramContentSlideDraftMatchingOwnerCandidates(
  workspaceId: string,
  candidates: readonly ProgramContentSlideOwnerRef[],
  options?: { scopeId?: string; allowedScopeIds?: readonly string[] }
): boolean {
  const sid = workspaceId.trim();
  if (!sid || !candidates.length) return false;
  const allowed = normalizeSlideDraftAllowedScopeIds(options);
  const candidateKeys = new Set(
    candidates
      .map((o) => {
        const id = o?.id?.trim();
        return id ? programContentSlidePresenceKey({ kind: o.kind, id }) : '';
      })
      .filter(Boolean)
  );
  for (const o of candidates) {
    const rec = readDraftRecord(sid, o);
    if (!rec || !slideDraftRecordHasLayouts(rec)) continue;
    if (!slideDraftRecordMatchesAllowedScope(rec, allowed)) continue;
    return true;
  }
  return listDraftRecords().some((rec) => {
    if ((rec.workspaceId ?? '').trim() !== sid) return false;
    if (!slideDraftRecordHasLayouts(rec)) return false;
    if (!slideDraftRecordMatchesAllowedScope(rec, allowed)) return false;
    const ownerId = rec.owner?.id?.trim();
    if (ownerId && candidateKeys.has(programContentSlidePresenceKey({ kind: rec.owner.kind, id: ownerId }))) {
      return true;
    }
    const patch = rec.learningPatch;
    const patchId = patch?.id?.trim();
    if (!patch || !patchId) return false;
    return candidateKeys.has(programContentSlidePresenceKey({ kind: patch.kind, id: patchId }));
  });
}

/** 로컬 draft 중 `page_index` ≥ 0(추가 슬라이드)에 실질 캔버스 내용이 있는지 */
export function programContentSlideOwnerHasMeaningfulExtraPageDraft(
  workspaceId: string,
  owner: ProgramContentSlideOwnerRef
): boolean {
  const layouts = getProgramContentSlideDraftLayouts(workspaceId, owner);
  for (const [idxRaw, layout] of Object.entries(layouts)) {
    const idx = Number(idxRaw);
    if (!Number.isFinite(idx) || idx < 0) continue;
    if (slideLayoutHasMeaningfulCanvasContent(layout)) return true;
  }
  return false;
}

export function discardPendingProgramContentSlideDraftForOwner(
  workspaceId: string,
  owner: ProgramContentSlideOwnerRef
): void {
  const sid = workspaceId.trim();
  if (!sid || !owner?.id?.trim()) return;
  removeDraftRecord(sid, owner);
}

/** 저장하지 않고 이탈 시 — 지정 scope(또는 전체) 슬라이드 초안을 제거한다. */
export function discardPendingProgramContentSlideDraftsForScopes(scopeIds?: readonly string[]): void {
  const drafts = listDraftRecords(scopeIds);
  if (!drafts.length) return;
  drafts.forEach((rec) => {
    const sid = (rec.workspaceId ?? '').trim();
    if (!sid || !rec.owner?.id?.trim()) return;
    removeDraftRecord(sid, rec.owner);
  });
}

export function queueProgramContentSlideCloneIntent(params: {
  workspaceId: string;
  scopeId: string;
  from: ProgramContentSlideOwnerRef;
  to: ProgramContentSlideOwnerRef;
}): void {
  const sid = params.workspaceId.trim();
  const scopeId = params.scopeId.trim();
  const fromId = params.from?.id?.trim();
  const toId = params.to?.id?.trim();
  if (!sid || !scopeId || !fromId || !toId) return;
  if (params.from.kind === params.to.kind && fromId === toId) return;
  writeCloneIntent({
    v: 1,
    workspaceId: sid,
    scopeId,
    from: { kind: params.from.kind, id: fromId },
    to: { kind: params.to.kind, id: toId },
    updatedAt: Date.now(),
  });
}

async function flushProgramContentSlideDraftRecord(rec: ProgramContentSlideDraftRecordV1): Promise<{
  ok: boolean;
  owner: { kind: 'program_cell' | 'fixed_row' | 'scoped_row'; id: string } | null;
  needsFullUiRefresh: boolean;
}> {
  const sid = (rec.workspaceId ?? '').trim();
  const owner = rec.owner;
  if (!sid || !owner?.id?.trim()) return { ok: false, owner: null, needsFullUiRefresh: false };

  const tableOwner =
    owner.kind === 'program_cell' || owner.kind === 'fixed_row' || owner.kind === 'scoped_row'
      ? { kind: owner.kind, id: owner.id.trim() }
      : null;

  if (rec.deleteAll) {
    await deleteProgramContentSlidePagesForOwner(sid, owner);
    if (rec.emptySlideClearMeta) {
      const meta = rec.emptySlideClearMeta;
      await clearPresentationRefBindingAfterEmptySlideSave({
        workspaceId: sid,
        presentationRefId: meta.presentationRefId,
        sourceOwner: meta.sourceOwner ?? null,
        bindingLearningText: meta.bindingLearningText,
        academicYear: meta.academicYear,
        termNumber: meta.termNumber,
        scopeId: rec.scopeId,
        annualCellKey: meta.annualCellKey,
        contextGradeId: meta.contextGradeId,
        createPresentationRefOnSave: meta.createPresentationRefOnSave,
      });
    }
    removeDraftRecord(sid, owner);
    /** empty clear는 바인딩 경로가 다양해 행 단위 캐시만으로 부족할 수 있음 */
    return { ok: true, owner: tableOwner, needsFullUiRefresh: !tableOwner || !!rec.emptySlideClearMeta };
  }

  const entries = Object.entries(rec.layoutsByPageIndex ?? {}).sort((a, b) => Number(a[0]) - Number(b[0]));
  const layoutsToPersist: Record<number, ProgramSlideLayoutV1> = {};
  for (const [idxRaw, layout] of entries) {
    const idx = Number(idxRaw);
    if (!Number.isFinite(idx) || idx < 0) continue;
    layoutsToPersist[idx] = await finalizePendingSlideImagesInLayout(parseSlideLayoutJson(layout));
  }

  let learningPatch = rec.learningPatch ?? null;
  if (Object.keys(layoutsToPersist).length) {
    if (learningPatch?.id?.trim() && learningPatch.learningText) {
      try {
        const mergedLearningText = await mergeLearningTextPatchWithCurrentDbBaseline({
          workspaceId: sid,
          kind: learningPatch.kind,
          id: learningPatch.id.trim(),
          learningText: learningPatch.learningText,
        });
        learningPatch = { ...learningPatch, learningText: mergedLearningText };
      } catch {
        /* baseline 조회 실패 시 기존 patch 그대로 시도 */
      }
    }
    await persistProgramContentSlideLayoutsForOwner(sid, owner, layoutsToPersist, learningPatch);
  }

  removeDraftRecord(sid, owner);

  const patchOwner =
    learningPatch?.id?.trim() &&
    (learningPatch.kind === 'program_cell' ||
      learningPatch.kind === 'fixed_row' ||
      learningPatch.kind === 'scoped_row')
      ? { kind: learningPatch.kind, id: learningPatch.id.trim() }
      : null;
  const rowOwner = patchOwner ?? tableOwner;
  const needsFullUiRefresh = !!learningPatch && !patchOwner && !tableOwner;
  return { ok: true, owner: rowOwner, needsFullUiRefresh };
}

export async function flushPendingProgramContentSlideDraftForOwner(
  workspaceId: string,
  owner: ProgramContentSlideOwnerRef
): Promise<number> {
  const sid = workspaceId.trim();
  if (!sid || !owner?.id?.trim()) return 0;
  const rec = readDraftRecord(sid, owner);
  if (!rec) return 0;
  try {
    return (await flushProgramContentSlideDraftRecord(rec)).ok ? 1 : 0;
  } catch {
    return 0;
  }
}

function stableLayoutJsonSignature(layout: ProgramSlideLayoutV1): string {
  try {
    return JSON.stringify(layout);
  } catch {
    return '';
  }
}

/** 슬라이드 탭 저장: learning_text 패치(선택) + 변경된 페이지만 upsert (캐시와 layout 비교) */
async function executePersistProgramContentSlideLayoutsBundle(
  workspaceId: string,
  owner: ProgramContentSlideOwnerRef,
  layouts: Record<number, ProgramSlideLayoutV1>,
  learningPatch?: ProgramSlideLearningTextPatch | null
): Promise<Record<number, ProgramSlideLayoutV1>> {
  const sid = workspaceId.trim();
  const oid = owner.id?.trim() ?? '';
  const finalizedLayouts: Record<number, ProgramSlideLayoutV1> = {};
  for (const [idxRaw, layout] of Object.entries(layouts)) {
    const idx = Number(idxRaw);
    if (!Number.isFinite(idx) || idx < 0) continue;
    finalizedLayouts[idx] = await finalizePendingSlideImagesInLayout(parseSlideLayoutJson(layout));
  }
  if (!sid || !oid) return finalizedLayouts;

  await assertSlideOwnerParentRowExists(sid, owner);
  if (learningPatch?.id?.trim()) {
    await assertSlideOwnerParentRowExists(sid, { kind: learningPatch.kind, id: learningPatch.id.trim() });
  }

  const sorted = Object.entries(finalizedLayouts)
    .filter(([idxRaw]) => {
      const idx = Number(idxRaw);
      return Number.isFinite(idx) && idx >= 0;
    })
    .sort((a, b) => Number(a[0]) - Number(b[0]));

  const cached = readProgramContentSlidePagesCache(sid, owner);
  const cachedByIndex = new Map<number, ProgramSlideLayoutV1>();
  if (cached?.rows?.length) {
    for (const row of cached.rows) {
      const pi = Number(row.page_index);
      if (!Number.isFinite(pi) || pi < 0) continue;
      cachedByIndex.set(pi, layoutFromDbRow(row));
    }
  }

  const pages = sorted
    .map(([idxRaw, layout]) => {
      const page_index = Number(idxRaw);
      const prev = cachedByIndex.get(page_index);
      if (prev && stableLayoutJsonSignature(prev) === stableLayoutJsonSignature(layout)) {
        return null;
      }
      return {
        page_index,
        layout_json: layout as unknown as Record<string, unknown>,
      };
    })
    .filter((p): p is { page_index: number; layout_json: Record<string, unknown> } => p != null);

  const keepCount = sorted.length;
  const prevCount = cachedByIndex.size;
  const needsTrim = keepCount < prevCount;
  const hasLearningPatch = Boolean(learningPatch?.id?.trim() && learningPatch.learningText);
  if (!pages.length && !needsTrim && !hasLearningPatch) {
    removeDraftRecord(sid, owner);
    setPresenceCached(sid, owner, keepCount > 0);
    return finalizedLayouts;
  }

  const learningRpc = learningPatch
    ? learningContentFieldsToRpcParams(
        await mergeLearningContentPatchWithCurrentDbBaseline({
          workspaceId: sid,
          kind: learningPatch.kind,
          id: learningPatch.id.trim(),
          learningText: learningPatch.learningText,
        }).catch(() => parseProgramLearningContentFieldsFromEditor(learningPatch.learningText))
      )
    : null;

  const { error: rpcError } = await supabase.rpc('save_program_content_slide_bundle', {
    p_school_id: sid,
    p_learning_kind: learningPatch?.kind ?? null,
    p_learning_id: learningPatch?.id?.trim() ?? null,
    p_learning_text: learningRpc?.p_learning_text ?? learningPatch?.learningText ?? null,
    p_learning_note: learningRpc?.p_learning_note ?? null,
    p_presentation_ref_id: learningRpc?.p_presentation_ref_id ?? null,
    p_worksheet_ref_id: learningRpc?.p_worksheet_ref_id ?? null,
    p_slide_owner_kind: owner.kind,
    p_slide_owner_id: oid,
    p_pages: pages,
  });

  if (rpcError) {
    const msg = rpcError.message ?? '';
    if (/save_program_content_slide_bundle|schema cache|function|PGRST202/i.test(msg)) {
      throw new Error(
        '슬라이드 일괄 저장 RPC가 없습니다. Supabase에 migration `20260523120000_save_program_content_slide_bundle.sql`을 적용해 주세요.'
      );
    }
    if (/foreign key constraint|violates foreign key/i.test(msg)) {
      throw new Error(
        'DB에 연결된 진도 셀·행이 없어 저장할 수 없습니다. 프로그램 표에서 해당 칸을 저장한 뒤, 이 탭을 닫고 Shift+U로 다시 열어 주세요.'
      );
    }
    throw new Error(msg);
  }

  /**
   * RPC가 누락 페이지를 자동 삭제하지 않는 환경 대비:
   * 현재 저장 번들(page_index 0..n-1) 밖의 뒤쪽 페이지는 명시적으로 제거한다.
   */
  const col =
    owner.kind === 'program_cell'
      ? 'program_progress_cell_id'
      : owner.kind === 'fixed_row'
        ? 'program_fixed_content_row_id'
        : owner.kind === 'presentation_ref'
          ? 'presentation_ref_id'
          : 'program_scoped_content_row_id';
  if (needsTrim || pages.length > 0 || keepCount === 0) {
    const { error: trimError } = await supabase
      .from('program_content_slide_pages')
      .delete()
      .eq('workspace_id', sid)
      .eq(col, oid)
      .gte('page_index', keepCount);
    if (trimError) throw new Error(trimError.message);
  }

  removeDraftRecord(sid, owner);
  /**
   * 삭제 대신 방금 저장한 레이아웃 + DB 메타(id/updated_at)로 캐시를 갱신.
   * 다음 재오픈 시 probe 서명이 맞아 layout_json 전체 select를 생략한다.
   * 메타 조회 실패 시에만 캐시를 지워 안전하게 전체 재조회로 떨어뜨린다.
   */
  await refreshProgramContentSlidePagesCacheAfterSave(sid, owner, finalizedLayouts, col, oid);
  setPresenceCached(sid, owner, sorted.length > 0);
  removePresentationOwnerStatsCacheKeys(sid, [programContentSlidePresenceKey(owner)]);
  invalidateAllPresentationAuditQueriesForWorkspace(sid);
  return finalizedLayouts;
}

export async function persistProgramContentSlideLayoutsForOwner(
  workspaceId: string,
  owner: ProgramContentSlideOwnerRef,
  layouts: Record<number, ProgramSlideLayoutV1>,
  learningPatch?: ProgramSlideLearningTextPatch | null
): Promise<Record<number, ProgramSlideLayoutV1>> {
  const sid = workspaceId.trim();
  const oid = owner?.id?.trim();
  if (!sid || !oid) return layouts;
  const bundleKey = `${sid}:${owner.kind}:${oid}:bundle`;
  const prev = slideBundleSaveLocks.get(bundleKey);
  const run = (prev ? prev.catch(() => undefined) : Promise.resolve()).then(() =>
    executePersistProgramContentSlideLayoutsBundle(sid, owner, layouts, learningPatch)
  );
  const tracked = run.finally(() => {
    if (slideBundleSaveLocks.get(bundleKey) === tracked) slideBundleSaveLocks.delete(bundleKey);
  });
  slideBundleSaveLocks.set(bundleKey, tracked);
  const persisted = await tracked;
  return persisted ?? layouts;
}

export type ProgramContentSlideFlushResult = {
  flushed: number;
  owners: Array<{ kind: 'program_cell' | 'fixed_row' | 'scoped_row'; id: string }>;
  needsFullUiRefresh: boolean;
};

export async function flushPendingProgramContentSlideDrafts(
  scopeIds?: readonly string[]
): Promise<ProgramContentSlideFlushResult> {
  /** `[]`는 listDraftRecords에서 스코프 필터가 꺼져 전체 draft가 잡히므로 no-op으로 처리 */
  if (Array.isArray(scopeIds) && scopeIds.length === 0) {
    return { flushed: 0, owners: [], needsFullUiRefresh: false };
  }
  const drafts = listDraftRecords(scopeIds);
  if (!drafts.length) return { flushed: 0, owners: [], needsFullUiRefresh: false };
  let flushed = 0;
  let needsFullUiRefresh = false;
  const owners: ProgramContentSlideFlushResult['owners'] = [];
  for (const rec of drafts) {
    try {
      const result = await flushProgramContentSlideDraftRecord(rec);
      if (result.ok) {
        flushed += 1;
        if (result.owner) owners.push(result.owner);
        if (result.needsFullUiRefresh) needsFullUiRefresh = true;
      }
    } catch {
      /**
       * 일부 슬라이드 draft flush 실패가 텍스트/행 저장 전체를 막지 않도록 격리.
       * 실패한 draft는 localStorage에 유지되어 다음 재시도까지 남을 수 있다.
       */
      continue;
    }
  }
  return { flushed, owners, needsFullUiRefresh };
}

function loadPresenceCacheBySchool(workspaceId: string): Record<string, 0 | 1> {
  const sid = workspaceId.trim();
  if (!sid) return {};
  const hit = presenceMemoryCache.get(sid);
  if (hit) return hit;
  if (typeof window === 'undefined') {
    const empty: Record<string, 0 | 1> = {};
    presenceMemoryCache.set(sid, empty);
    return empty;
  }
  try {
    const raw = window.localStorage.getItem(PROGRAM_CONTENT_SLIDE_PRESENCE_CACHE_PREFIX + sid);
    const parsed = raw ? (JSON.parse(raw) as Record<string, 0 | 1>) : {};
    const normalized = parsed && typeof parsed === 'object' ? parsed : {};
    presenceMemoryCache.set(sid, normalized);
    return normalized;
  } catch {
    const empty: Record<string, 0 | 1> = {};
    presenceMemoryCache.set(sid, empty);
    return empty;
  }
}

function savePresenceCacheBySchool(workspaceId: string, map: Record<string, 0 | 1>): void {
  const sid = workspaceId.trim();
  if (!sid) return;
  presenceMemoryCache.set(sid, map);
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(PROGRAM_CONTENT_SLIDE_PRESENCE_CACHE_PREFIX + sid, JSON.stringify(map));
  } catch {
    /* quota 등은 무시 */
  }
}

export function invalidateProgramContentSlidePresenceCache(workspaceId: string): void {
  const sid = workspaceId.trim();
  if (!sid) return;
  presenceMemoryCache.delete(sid);
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.removeItem(PROGRAM_CONTENT_SLIDE_PRESENCE_CACHE_PREFIX + sid);
    } catch {
      /* ignore */
    }
  }
  invalidateProgramPresentationListStatsCache(sid);
  invalidatePresentationOwnerStatsQueries(sid);
  invalidateAllPresentationAuditQueriesForWorkspace(sid);
}

function setPresenceCached(workspaceId: string, owner: ProgramContentSlideOwnerRef, present: boolean): void {
  const sid = workspaceId.trim();
  if (!sid || !owner?.id?.trim()) return;
  const cache = { ...loadPresenceCacheBySchool(sid) };
  cache[programContentSlidePresenceKey(owner)] = present ? 1 : 0;
  savePresenceCacheBySchool(sid, cache);
}

export interface ProgramContentSlidePageRow {
  id: string;
  workspace_id: string;
  page_index: number;
  layout_json: unknown;
  updated_at: string;
  program_progress_cell_id: string | null;
  program_fixed_content_row_id: string | null;
  program_scoped_content_row_id: string | null;
  presentation_ref_id: string | null;
}

interface ProgramContentSlideLayoutCachePayloadV1 {
  v: 1;
  workspaceId: string;
  owner: ProgramContentSlideOwnerRef;
  updatedAtSignature: string;
  rows: ProgramContentSlidePageRow[];
}

function slideLayoutCacheKey(workspaceId: string, owner: ProgramContentSlideOwnerRef): string {
  return `${PROGRAM_CONTENT_SLIDE_LAYOUT_CACHE_PREFIX}${workspaceId.trim()}:${owner.kind}:${owner.id.trim()}`;
}

export function buildSlideRowsUpdatedAtSignature(
  rows: Array<Pick<ProgramContentSlidePageRow, 'page_index' | 'updated_at'>>
): string {
  if (!rows.length) return '0';
  const sorted = [...rows].sort((a, b) => a.page_index - b.page_index);
  return sorted.map((row) => `${row.page_index}:${String(row.updated_at ?? '').trim()}`).join('|');
}

function writeProgramContentSlidePagesCache(
  workspaceId: string,
  owner: ProgramContentSlideOwnerRef,
  rows: ProgramContentSlidePageRow[]
): void {
  const sid = workspaceId.trim();
  const oid = owner?.id?.trim();
  const storage = draftStorage();
  if (!sid || !oid || !storage) return;
  try {
    const payload: ProgramContentSlideLayoutCachePayloadV1 = {
      v: 1,
      workspaceId: sid,
      owner: { kind: owner.kind, id: oid },
      updatedAtSignature: buildSlideRowsUpdatedAtSignature(rows),
      rows,
    };
    storage.setItem(slideLayoutCacheKey(sid, owner), JSON.stringify(payload));
  } catch {
    /* quota 등은 무시 */
  }
}

function readProgramContentSlidePagesCache(
  workspaceId: string,
  owner: ProgramContentSlideOwnerRef
): ProgramContentSlideLayoutCachePayloadV1 | null {
  const sid = workspaceId.trim();
  const oid = owner?.id?.trim();
  const storage = draftStorage();
  if (!sid || !oid || !storage) return null;
  try {
    const raw = storage.getItem(slideLayoutCacheKey(sid, owner));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ProgramContentSlideLayoutCachePayloadV1;
    if (!parsed || parsed.v !== 1) return null;
    if ((parsed.workspaceId ?? '').trim() !== sid) return null;
    if (parsed.owner?.kind !== owner.kind || (parsed.owner?.id ?? '').trim() !== oid) return null;
    if (!Array.isArray(parsed.rows)) return null;

    const rows: ProgramContentSlidePageRow[] = [];
    for (const rawRow of parsed.rows) {
      if (!rawRow || typeof rawRow !== 'object') return null;
      const row = rawRow as Partial<ProgramContentSlidePageRow>;
      const pageIndex = Number(row.page_index);
      if (!Number.isFinite(pageIndex) || pageIndex < 0) return null;
      rows.push({
        id: String(row.id ?? '').trim(),
        workspace_id: String(row.workspace_id ?? '').trim(),
        page_index: pageIndex,
        layout_json: row.layout_json,
        updated_at: String(row.updated_at ?? '').trim(),
        program_progress_cell_id: row.program_progress_cell_id ? String(row.program_progress_cell_id) : null,
        program_fixed_content_row_id: row.program_fixed_content_row_id ? String(row.program_fixed_content_row_id) : null,
        program_scoped_content_row_id: row.program_scoped_content_row_id ? String(row.program_scoped_content_row_id) : null,
        presentation_ref_id: row.presentation_ref_id ? String(row.presentation_ref_id) : null,
      });
    }
    return {
      v: 1,
      workspaceId: sid,
      owner: { kind: owner.kind, id: oid },
      updatedAtSignature: buildSlideRowsUpdatedAtSignature(rows),
      rows,
    };
  } catch {
    return null;
  }
}

/** 방금 저장·조회한 페이지 버전(캐시 기준). 캐시가 없으면 null — 다음 저장의 충돌 비교 기준으로 쓴다. */
export function getProgramContentSlideCachedSignature(
  workspaceId: string,
  owner: ProgramContentSlideOwnerRef
): string | null {
  return readProgramContentSlidePagesCache(workspaceId, owner)?.updatedAtSignature ?? null;
}

/**
 * 저장 직전 충돌 확인: page_index·updated_at만 조회해(레이아웃 본문 없음) 화면을 연 뒤 다른 탭·기기가 저장했는지 본다.
 */
export async function fetchProgramContentSlideRemoteSignature(
  workspaceId: string,
  owner: ProgramContentSlideOwnerRef
): Promise<string> {
  const sid = workspaceId.trim();
  const oid = owner.id?.trim() ?? '';
  const col =
    owner.kind === 'program_cell'
      ? 'program_progress_cell_id'
      : owner.kind === 'fixed_row'
        ? 'program_fixed_content_row_id'
        : owner.kind === 'presentation_ref'
          ? 'presentation_ref_id'
          : 'program_scoped_content_row_id';
  const { data, error } = await supabase
    .from('program_content_slide_pages')
    .select('page_index, updated_at')
    .eq('workspace_id', sid)
    .eq(col, oid)
    .order('page_index', { ascending: true });
  if (error) throw new Error(error.message);
  return buildSlideRowsUpdatedAtSignature(
    ((data ?? []) as Array<{ page_index?: number; updated_at?: string | null }>)
      .map((row) => ({ page_index: Number(row.page_index), updated_at: String(row.updated_at ?? '').trim() }))
      .filter((row) => Number.isFinite(row.page_index) && row.page_index >= 0)
  );
}

function removeProgramContentSlidePagesCache(workspaceId: string, owner: ProgramContentSlideOwnerRef): void {
  const sid = workspaceId.trim();
  const oid = owner?.id?.trim();
  const storage = draftStorage();
  if (!sid || !oid || !storage) return;
  try {
    storage.removeItem(slideLayoutCacheKey(sid, owner));
  } catch {
    /* ignore */
  }
}

/**
 * 번들 저장 직후: layout_json은 이미 알고 있으므로 id·updated_at만 조회해 캐시를 맞춘다.
 * (삭제 후 재오픈 전체 select보다 egress가 훨씬 작음)
 */
async function refreshProgramContentSlidePagesCacheAfterSave(
  workspaceId: string,
  owner: ProgramContentSlideOwnerRef,
  layouts: Record<number, ProgramSlideLayoutV1>,
  ownerCol: string,
  ownerId: string
): Promise<void> {
  const sid = workspaceId.trim();
  const oid = ownerId.trim();
  if (!sid || !oid) return;

  try {
    const { data, error } = await supabase
      .from('program_content_slide_pages')
      .select('id, page_index, updated_at')
      .eq('workspace_id', sid)
      .eq(ownerCol, oid)
      .order('page_index', { ascending: true });

    if (error) {
      console.warn('[programContentSlide] 저장 후 캐시 갱신용 메타 조회 실패 → 캐시 삭제', error.message);
      removeProgramContentSlidePagesCache(sid, owner);
      return;
    }

    const cols = ownerColumns(owner);
    const metaRows = (data ?? []) as Array<{
      id?: string;
      page_index?: number;
      updated_at?: string | null;
    }>;

    const rows: ProgramContentSlidePageRow[] = [];
    for (const meta of metaRows) {
      const pageIndex = Number(meta.page_index);
      if (!Number.isFinite(pageIndex) || pageIndex < 0) continue;
      const layout = layouts[pageIndex];
      if (!layout) continue;
      rows.push({
        id: String(meta.id ?? '').trim(),
        workspace_id: sid,
        page_index: pageIndex,
        layout_json: layout as unknown as Record<string, unknown>,
        updated_at: String(meta.updated_at ?? '').trim(),
        program_progress_cell_id: cols.program_progress_cell_id,
        program_fixed_content_row_id: cols.program_fixed_content_row_id,
        program_scoped_content_row_id: cols.program_scoped_content_row_id,
        presentation_ref_id: cols.presentation_ref_id,
      });
    }

    writeProgramContentSlidePagesCache(sid, owner, rows);
  } catch (err) {
    console.warn('[programContentSlide] 저장 후 캐시 갱신 실패 → 캐시 삭제', err);
    removeProgramContentSlidePagesCache(sid, owner);
  }
}

function ownerColumns(owner: ProgramContentSlideOwnerRef): {
  program_progress_cell_id: string | null;
  program_fixed_content_row_id: string | null;
  program_scoped_content_row_id: string | null;
  presentation_ref_id: string | null;
} {
  if (owner.kind === 'program_cell') {
    return { program_progress_cell_id: owner.id, program_fixed_content_row_id: null, program_scoped_content_row_id: null, presentation_ref_id: null };
  }
  if (owner.kind === 'fixed_row') {
    return { program_progress_cell_id: null, program_fixed_content_row_id: owner.id, program_scoped_content_row_id: null, presentation_ref_id: null };
  }
  if (owner.kind === 'presentation_ref') {
    return { program_progress_cell_id: null, program_fixed_content_row_id: null, program_scoped_content_row_id: null, presentation_ref_id: owner.id };
  }
  return { program_progress_cell_id: null, program_fixed_content_row_id: null, program_scoped_content_row_id: owner.id, presentation_ref_id: null };
}

/** 진도셀·스코프·고정 행이 DB에 있는지(프레젠테이션 ref는 UUID만 검사) */
export async function slideOwnerParentRowExistsInDb(
  workspaceId: string,
  owner: ProgramContentSlideOwnerRef
): Promise<boolean> {
  const sid = workspaceId.trim();
  const oid = owner.id?.trim() ?? '';
  if (!sid || !oid || !isPersistableUuid(oid)) return false;
  if (owner.kind === 'presentation_ref') return true;
  const table =
    owner.kind === 'program_cell'
      ? 'program_progress_cells'
      : owner.kind === 'fixed_row'
        ? 'program_fixed_content_rows'
        : owner.kind === 'scoped_row'
          ? 'program_scoped_content_rows'
          : null;
  if (!table) return false;
  const { data, error } = await supabase.from(table).select('id').eq('id', oid).maybeSingle();
  if (error || !data) return false;
  return true;
}

/**
 * 슬라이드 탭을 열 때: presence가 옛 program_cell을 가리켜도 DB에 행이 없으면
 * presentation_ref → scoped_row → fixed_row → program_cell 순으로 살아 있는 owner를 고른다.
 */
async function pickSlideOwnerWithMostPagesInDb(
  workspaceId: string,
  candidates: ProgramContentSlideOwnerRef[]
): Promise<ProgramContentSlideOwnerRef | null> {
  const sid = workspaceId.trim();
  if (!sid || !candidates.length) return null;
  const tryOrder: ProgramContentSlideOwnerRef['kind'][] = [
    'presentation_ref',
    'scoped_row',
    'fixed_row',
    'program_cell',
  ];
  let best: { owner: ProgramContentSlideOwnerRef; count: number } | null = null;
  for (const kind of tryOrder) {
    for (const owner of candidates) {
      if (owner.kind !== kind) continue;
      if (!(await slideOwnerParentRowExistsInDb(sid, owner))) continue;
      const pages = await fetchProgramContentSlidePages(sid, owner, { forceRefresh: true });
      const count = pages.length;
      if (count > 0 && (!best || count > best.count)) {
        best = { owner, count };
      }
    }
  }
  return best?.owner ?? null;
}

export async function resolveSlideTabOwnerForSave(params: {
  workspaceId: string;
  candidates: ProgramContentSlideOwnerRef[];
  presenceKeys: ReadonlySet<string>;
  presenceKeyOf: (o: ProgramContentSlideOwnerRef) => string;
  fallbackOwner: ProgramContentSlideOwnerRef | null;
}): Promise<ProgramContentSlideOwnerRef | null> {
  const { workspaceId, candidates, presenceKeys, presenceKeyOf, fallbackOwner } = params;
  const sid = workspaceId.trim();
  if (!sid) return null;

  const uniq: ProgramContentSlideOwnerRef[] = [];
  const seen = new Set<string>();
  for (const c of candidates) {
    if (!c?.id?.trim()) continue;
    const k = presenceKeyOf(c);
    if (seen.has(k)) continue;
    seen.add(k);
    uniq.push(c);
  }

  const ownerWithPages = await pickSlideOwnerWithMostPagesInDb(sid, uniq);
  if (ownerWithPages) return ownerWithPages;

  const preferred = pickProgramContentSlideOwnerWithDbPresence(
    uniq,
    presenceKeys,
    presenceKeyOf,
    fallbackOwner
  );

  let preferredExists = false;
  if (preferred) {
    preferredExists = await slideOwnerParentRowExistsInDb(sid, preferred);
    if (preferredExists) return preferred;
    if (preferred.kind === 'program_cell') {
      setPresenceCached(sid, preferred, false);
      invalidateProgramContentSlidePresenceCache(sid);
    }
  }

  const tryOrder: ProgramContentSlideOwnerRef['kind'][] = [
    'presentation_ref',
    'scoped_row',
    'fixed_row',
    'program_cell',
  ];
  for (const kind of tryOrder) {
    for (const c of uniq) {
      if (c.kind !== kind) continue;
      if (preferred && !preferredExists && c.kind === preferred.kind && c.id.trim() === preferred.id.trim()) {
        continue;
      }
      if (await slideOwnerParentRowExistsInDb(sid, c)) return c;
    }
  }
  if (fallbackOwner && (await slideOwnerParentRowExistsInDb(sid, fallbackOwner))) return fallbackOwner;
  return null;
}

async function assertSlideOwnerParentRowExists(workspaceId: string, owner: ProgramContentSlideOwnerRef): Promise<void> {
  const sid = workspaceId.trim();
  const oid = owner.id?.trim() ?? '';
  if (!sid || !oid || !isPersistableUuid(oid)) {
    throw new Error('슬라이드 저장에 쓸 owner id가 올바르지 않습니다. 탭을 닫고 Shift+U로 다시 열어 주세요.');
  }
  if (owner.kind === 'presentation_ref') {
    if (!(await slideOwnerParentRowExistsInDb(sid, owner))) {
      throw new Error('슬라이드 저장에 쓸 owner id가 올바르지 않습니다. 탭을 닫고 Shift+U로 다시 열어 주세요.');
    }
    return;
  }
  if (await slideOwnerParentRowExistsInDb(sid, owner)) return;
  const hint =
    owner.kind === 'program_cell'
      ? '진도 셀(program_progress_cells)'
      : owner.kind === 'fixed_row'
        ? '고정 프로그램 행'
        : '편제·프로젝트 행(program_scoped_content_rows)';
  throw new Error(
    `DB에 ${hint}이(가) 없어 슬라이드를 저장할 수 없습니다. 프로그램 표에서 해당 칸을 저장한 뒤, 이 탭을 닫고 Shift+U로 다시 열어 주세요.`
  );
}

export async function fetchProgramContentSlidePages(
  workspaceId: string,
  owner: ProgramContentSlideOwnerRef,
  options?: { forceRefresh?: boolean }
): Promise<ProgramContentSlidePageRow[]> {
  const sid = workspaceId.trim();
  const oid = owner.id?.trim() ?? '';
  if (!sid || !oid) return [];
  const cols = ownerColumns(owner);
  const col =
    owner.kind === 'program_cell'
      ? 'program_progress_cell_id'
      : owner.kind === 'fixed_row'
        ? 'program_fixed_content_row_id'
        : owner.kind === 'presentation_ref'
          ? 'presentation_ref_id'
          : 'program_scoped_content_row_id';
  const id =
    owner.kind === 'program_cell'
      ? cols.program_progress_cell_id!
      : owner.kind === 'fixed_row'
        ? cols.program_fixed_content_row_id!
        : owner.kind === 'presentation_ref'
          ? cols.presentation_ref_id!
          : cols.program_scoped_content_row_id!;

  const cached = options?.forceRefresh ? null : readProgramContentSlidePagesCache(sid, owner);
  if (cached) {
    const { data: probeData, error: probeError } = await supabase
      .from('program_content_slide_pages')
      .select('page_index, updated_at')
      .eq('workspace_id', sid)
      .eq(col, id)
      .order('page_index', { ascending: true });
    if (probeError) {
      if (owner.kind === 'presentation_ref' && /presentation_ref_id/i.test(probeError.message ?? '')) {
        throw new Error('프레젠테이션 연결 migration이 아직 적용되지 않았습니다.');
      }
      throw new Error(probeError.message);
    }
    const probeRows = ((probeData ?? []) as Array<{ page_index?: number; updated_at?: string | null }>)
      .map((row) => ({
        page_index: Number(row.page_index),
        updated_at: String(row.updated_at ?? '').trim(),
      }))
      .filter((row) => Number.isFinite(row.page_index) && row.page_index >= 0);
    const probeSignature = buildSlideRowsUpdatedAtSignature(
      probeRows as Array<Pick<ProgramContentSlidePageRow, 'page_index' | 'updated_at'>>
    );
    if (probeSignature === cached.updatedAtSignature) {
      setPresenceCached(
        sid,
        owner,
        cached.rows.some((row) => slideLayoutHasMeaningfulCanvasContent(layoutFromDbRow(row)))
      );
      return cached.rows;
    }
    if (!probeRows.length) {
      writeProgramContentSlidePagesCache(sid, owner, []);
      setPresenceCached(sid, owner, false);
      return [];
    }
  }

  const selectColumns =
    owner.kind === 'presentation_ref'
      ? 'id, workspace_id, page_index, layout_json, updated_at, program_progress_cell_id, program_fixed_content_row_id, program_scoped_content_row_id, presentation_ref_id'
      : 'id, workspace_id, page_index, layout_json, updated_at, program_progress_cell_id, program_fixed_content_row_id, program_scoped_content_row_id';

  const { data, error } = await supabase
    .from('program_content_slide_pages')
    .select(selectColumns)
    .eq('workspace_id', sid)
    .eq(col, id)
    .order('page_index', { ascending: true });

  if (error) {
    if (owner.kind === 'presentation_ref' && /presentation_ref_id/i.test(error.message ?? '')) {
      throw new Error('프레젠테이션 연결 migration이 아직 적용되지 않았습니다.');
    }
    throw new Error(error.message);
  }
  const rows = ((data ?? []) as unknown as Array<
    Omit<ProgramContentSlidePageRow, 'presentation_ref_id'> & { presentation_ref_id?: string | null }
  >).map((row) => ({
    ...row,
    updated_at: String((row as { updated_at?: unknown }).updated_at ?? '').trim(),
    presentation_ref_id: row.presentation_ref_id ?? null,
  }));
  writeProgramContentSlidePagesCache(sid, owner, rows);
  setPresenceCached(
    sid,
    owner,
    rows.some((row) => slideLayoutHasMeaningfulCanvasContent(layoutFromDbRow(row)))
  );
  return rows;
}

function slidePageSaveLockKey(params: {
  workspaceId: string;
  owner: ProgramContentSlideOwnerRef;
  pageIndex: number;
}): string {
  return `${params.workspaceId.trim()}:${params.owner.kind}:${params.owner.id.trim()}:${params.pageIndex}`;
}

async function persistProgramContentSlidePage(params: {
  workspaceId: string;
  owner: ProgramContentSlideOwnerRef;
  pageIndex: number;
  layout: ProgramSlideLayoutV1;
}): Promise<void> {
  const { workspaceId, owner, pageIndex, layout } = params;
  await assertSlideOwnerParentRowExists(workspaceId, owner);
  const cols = ownerColumns(owner);
  const id =
    owner.kind === 'program_cell'
      ? cols.program_progress_cell_id!
      : owner.kind === 'fixed_row'
        ? cols.program_fixed_content_row_id!
        : owner.kind === 'presentation_ref'
          ? cols.presentation_ref_id!
          : cols.program_scoped_content_row_id!;

  const layout_json = layout as unknown as Record<string, unknown>;
  const { error: rpcError } = await supabase.rpc('upsert_program_content_slide_page', {
    p_school_id: workspaceId,
    p_owner_kind: owner.kind,
    p_owner_id: id,
    p_page_index: pageIndex,
    p_layout_json: layout_json,
  });
  if (!rpcError) {
    removeProgramContentSlidePagesCache(workspaceId, owner);
    setPresenceCached(workspaceId, owner, true);
    removePresentationOwnerStatsCacheKeys(workspaceId.trim(), [programContentSlidePresenceKey(owner)]);
    invalidateAllPresentationAuditQueriesForWorkspace(workspaceId.trim());
    return;
  }
  if (/upsert_program_content_slide_page|schema cache|function/i.test(rpcError.message ?? '')) {
    throw new Error('프레젠테이션 저장 RPC가 없습니다. 최신 Supabase migration을 적용해 주세요.');
  }
  if (/foreign key constraint|violates foreign key/i.test(rpcError.message ?? '')) {
    throw new Error(
      'DB에 연결된 진도 셀·행이 없어 저장할 수 없습니다. 프로그램 표에서 해당 칸을 저장한 뒤, 이 탭을 닫고 Shift+U로 다시 열어 주세요.'
    );
  }
  throw new Error(rpcError.message);
}

export async function upsertProgramContentSlidePage(params: {
  workspaceId: string;
  owner: ProgramContentSlideOwnerRef;
  pageIndex: number;
  layout: ProgramSlideLayoutV1;
}): Promise<void> {
  const key = slidePageSaveLockKey(params);
  const prev = slidePageSaveLocks.get(key);
  const run = (prev ? prev.catch(() => undefined) : Promise.resolve()).then(() =>
    persistProgramContentSlidePage(params)
  );
  const tracked = run.finally(() => {
    if (slidePageSaveLocks.get(key) === tracked) slidePageSaveLocks.delete(key);
  });
  slidePageSaveLocks.set(key, tracked);
  return tracked;
}

export function layoutFromDbRow(row: ProgramContentSlidePageRow): ProgramSlideLayoutV1 {
  return parseSlideLayoutJson(row.layout_json);
}

export function programContentSlidePresenceKey(owner: ProgramContentSlideOwnerRef): string {
  return `${owner.kind}:${owner.id}`;
}

function chunkIds<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

function meaningfulExtraSlideOwnerIdsFromRows(
  rows: Array<Record<string, unknown>>,
  idField: 'program_progress_cell_id' | 'program_fixed_content_row_id' | 'program_scoped_content_row_id' | 'presentation_ref_id'
): Set<string> {
  const ids = new Set<string>();
  for (const r of rows) {
    const id = String(r[idField] ?? '').trim();
    const pi = Number(r.page_index);
    if (!id || !Number.isFinite(pi) || pi < 0) continue;
    if (!slideLayoutHasMeaningfulCanvasContent(parseSlideLayoutJson(r.layout_json))) continue;
    ids.add(id);
  }
  return ids;
}

const pendingProgramContentSlidePresenceFetches = new Map<string, Promise<Set<string>>>();

function programContentSlidePresenceFetchDedupeKey(
  workspaceId: string,
  owners: ProgramContentSlideOwnerRef[],
  forceRefresh: boolean
): string {
  const seen = new Set<string>();
  const keys: string[] = [];
  for (const o of owners) {
    if (!o?.id?.trim()) continue;
    const k = programContentSlidePresenceKey(o);
    if (seen.has(k)) continue;
    seen.add(k);
    keys.push(k);
  }
  keys.sort();
  return `${workspaceId.trim()}\u0001${forceRefresh ? '1' : '0'}\u0001${keys.join('\u0001')}`;
}

/** 학습내용(owner)별로 DB 추가 슬라이드(`page_index` ≥ 0)에 실질 캔버스 데이터가 있으면 해당 키를 Set에 넣는다. */
async function executeProgramContentSlidePresenceKeyFetch(
  workspaceId: string,
  owners: ProgramContentSlideOwnerRef[],
  options?: { forceRefresh?: boolean; cacheOnly?: boolean }
): Promise<Set<string>> {
  const out = new Set<string>();
  const sid = workspaceId.trim();
  if (!sid || !owners.length) return out;
  /**
   * cacheOnly는 DB를 치지 않되, 다른 탭이 저장한 LS presence는 읽어야 한다.
   * 메모리 캐시만 쓰면 수업 프레젠 저장 직후 메인 프레젠이 옛 owner로 빈 캔버스를 연다.
   */
  if (options?.cacheOnly) presenceMemoryCache.delete(sid);
  const cached = { ...loadPresenceCacheBySchool(sid) };
  const seen = new Set<string>();
  const uniqueAll = owners.filter((o) => {
    if (!o?.id?.trim()) return false;
    const k = programContentSlidePresenceKey(o);
    if (seen.has(k)) return false;
    seen.add(k);
    /**
     * presence는 워크스페이스(`workspace_id`)별 localStorage(`PROGRAM_CONTENT_SLIDE_PRESENCE_CACHE_PREFIX`)에 0/1로 보관한다.
     * 알려진 키는 DB를 다시 타지 않아 탭 전환·재마운트 시 불필요한 네트워크를 줄인다.
     * 저장·삭제·복제 등 슬라이드 변경 경로에서 `setPresenceCached`로 동기화하고, 필요 시 `forceRefresh`로 무시한다.
     */
    if (!options?.forceRefresh && cached[k] === 1) out.add(k);
    return true;
  });
  /** 메인 프레젠 등: LS에 있는 presence만 쓰고 DB는 절대 치지 않음 */
  if (options?.cacheOnly) return out;
  const unique = options?.forceRefresh
    ? uniqueAll
    : uniqueAll.filter((o) => {
        const k = programContentSlidePresenceKey(o);
        return cached[k] == null;
      });
  if (!unique.length) return out;
  const cells = unique.filter((o) => o.kind === 'program_cell').map((o) => o.id);
  const fixed = unique.filter((o) => o.kind === 'fixed_row').map((o) => o.id);
  const scoped = unique.filter((o) => o.kind === 'scoped_row').map((o) => o.id);
  const presentationRefs = unique.filter((o) => o.kind === 'presentation_ref').map((o) => o.id);

  for (const batch of chunkIds(cells, PRESENCE_IN_CHUNK)) {
    if (!batch.length) continue;
    const { data, error } = await supabase
      .from('program_content_slide_pages')
      .select('program_progress_cell_id, page_index, layout_json')
      .eq('workspace_id', sid)
      .in('program_progress_cell_id', batch);
    if (error) throw new Error(error.message);
    const foundSet = meaningfulExtraSlideOwnerIdsFromRows(
      (data ?? []) as Array<Record<string, unknown>>,
      'program_progress_cell_id'
    );
    batch.forEach((id) => {
      const k = programContentSlidePresenceKey({ kind: 'program_cell', id });
      if (foundSet.has(id)) {
        out.add(k);
        cached[k] = 1;
      } else {
        cached[k] = 0;
      }
    });
  }
  for (const batch of chunkIds(fixed, PRESENCE_IN_CHUNK)) {
    if (!batch.length) continue;
    const { data, error } = await supabase
      .from('program_content_slide_pages')
      .select('program_fixed_content_row_id, page_index, layout_json')
      .eq('workspace_id', sid)
      .in('program_fixed_content_row_id', batch);
    if (error) throw new Error(error.message);
    const foundSet = meaningfulExtraSlideOwnerIdsFromRows(
      (data ?? []) as Array<Record<string, unknown>>,
      'program_fixed_content_row_id'
    );
    batch.forEach((id) => {
      const k = programContentSlidePresenceKey({ kind: 'fixed_row', id });
      if (foundSet.has(id)) {
        out.add(k);
        cached[k] = 1;
      } else {
        cached[k] = 0;
      }
    });
  }
  for (const batch of chunkIds(scoped, PRESENCE_IN_CHUNK)) {
    if (!batch.length) continue;
    const { data, error } = await supabase
      .from('program_content_slide_pages')
      .select('program_scoped_content_row_id, page_index, layout_json')
      .eq('workspace_id', sid)
      .in('program_scoped_content_row_id', batch);
    if (error) throw new Error(error.message);
    const foundSet = meaningfulExtraSlideOwnerIdsFromRows(
      (data ?? []) as Array<Record<string, unknown>>,
      'program_scoped_content_row_id'
    );
    batch.forEach((id) => {
      const k = programContentSlidePresenceKey({ kind: 'scoped_row', id });
      if (foundSet.has(id)) {
        out.add(k);
        cached[k] = 1;
      } else {
        cached[k] = 0;
      }
    });
  }
  for (const batch of chunkIds(presentationRefs, PRESENCE_IN_CHUNK)) {
    if (!batch.length) continue;
    const { data, error } = await supabase
      .from('program_content_slide_pages')
      .select('presentation_ref_id, page_index, layout_json')
      .eq('workspace_id', sid)
      .in('presentation_ref_id', batch);
    if (error) {
      if (/presentation_ref_id/i.test(error.message ?? '')) continue;
      throw new Error(error.message);
    }
    const foundSet = meaningfulExtraSlideOwnerIdsFromRows(
      (data ?? []) as Array<Record<string, unknown>>,
      'presentation_ref_id'
    );
    batch.forEach((id) => {
      const k = programContentSlidePresenceKey({ kind: 'presentation_ref', id });
      if (foundSet.has(id)) {
        out.add(k);
        cached[k] = 1;
      } else {
        cached[k] = 0;
      }
    });
  }
  savePresenceCacheBySchool(sid, cached);
  return out;
}

export async function fetchProgramContentSlidePresenceKeySet(
  workspaceId: string,
  owners: ProgramContentSlideOwnerRef[],
  options?: { forceRefresh?: boolean; cacheOnly?: boolean }
): Promise<Set<string>> {
  const sid = workspaceId.trim();
  if (!sid || !owners.length) return new Set();
  if (options?.cacheOnly) {
    return executeProgramContentSlidePresenceKeyFetch(workspaceId, owners, { cacheOnly: true });
  }
  const dedupeKey = programContentSlidePresenceFetchDedupeKey(sid, owners, !!options?.forceRefresh);
  const inflight = pendingProgramContentSlidePresenceFetches.get(dedupeKey);
  if (inflight) return inflight;
  const task = executeProgramContentSlidePresenceKeyFetch(workspaceId, owners, options).finally(() => {
    pendingProgramContentSlidePresenceFetches.delete(dedupeKey);
  });
  pendingProgramContentSlidePresenceFetches.set(dedupeKey, task);
  return task;
}

function newPresentationForkRefId(): string {
  const id = globalThis.crypto?.randomUUID?.();
  if (id) return id;
  return '00000000-0000-4000-8000-000000000000'.replace(/[08]/g, (c) => {
    const n = Number(c);
    const r = Math.floor(Math.random() * 16);
    return (n ^ (r & (15 >> (n / 4)))).toString(16);
  });
}

/** 행 복제 시 learning_text가 동일 presentation_ref를 가리키면 슬라이드가 공유되므로 새 ref로 분리한다. */
export function forkPresentationRefInLearningText(raw: string | null | undefined): {
  nextLearningText: string;
  fromRef: string;
  toRef: string;
} | null {
  const fromRef = resolvePresentationRefIdFromLearningText(raw ?? '');
  if (!fromRef) return null;
  const toRef = newPresentationForkRefId();
  return {
    nextLearningText: learningTextWithPresentationRef(raw ?? '', toRef),
    fromRef,
    toRef,
  };
}

/** learning_text JSON에 presentationRefId를 넣되 기존 worksheetRefId·메모 등은 유지 */
export function learningTextWithPresentationRef(text: string | null | undefined, refId: string): string {
  const rid = refId.trim();
  if (!rid || !isPersistableUuid(rid)) return textForStorage(text) ?? '';
  const p = parseMemoFieldStorageDeep(text ?? '');
  const payload: {
    text: string;
    note?: string;
    behavior_note?: string;
    presentationRefId: string;
    worksheetRefId?: string;
  } = {
    text: p.text,
    presentationRefId: rid,
  };
  if (p.note) payload.note = p.note;
  if (p.behaviorNote) payload.behavior_note = p.behaviorNote;
  if (p.worksheetRefId) payload.worksheetRefId = p.worksheetRefId;
  return JSON.stringify(payload);
}

/** DB 저장용: learning_text에서 presentationRefId만 제거(복제 fork 실패 시 동일 ref 공유 방지). */
export function learningTextStorageWithoutPresentationRef(raw: string | null | undefined): string | null {
  if (!resolvePresentationRefIdFromLearningText(raw ?? '')) return textForStorage(raw);
  const p = parseMemoFieldStorageDeep(raw ?? '');
  const payload: {
    text: string;
    note?: string;
    behavior_note?: string;
    worksheetRefId?: string;
  } = { text: p.text };
  if (p.note) payload.note = p.note;
  if (p.behaviorNote) payload.behavior_note = p.behaviorNote;
  if (p.worksheetRefId) payload.worksheetRefId = p.worksheetRefId;
  return textForStorage(JSON.stringify(payload));
}

/** 통합·복제: from owner의 모든 슬라이드 페이지를 to owner에 동일 page_index로 복사(이미 있으면 upsert 덮어쓰기) */
export async function copyProgramContentSlidePagesBetweenOwners(
  workspaceId: string,
  from: ProgramContentSlideOwnerRef,
  to: ProgramContentSlideOwnerRef
): Promise<void> {
  const pages = await fetchProgramContentSlidePages(workspaceId, from);
  if (!pages.length) return;
  for (const p of pages) {
    await upsertProgramContentSlidePage({
      workspaceId,
      owner: to,
      pageIndex: p.page_index,
      layout: layoutFromDbRow(p),
    });
  }
  setPresenceCached(workspaceId, to, true);
}

export async function forkPresentationRefAfterScopedOrFixedRowDuplicate(params: {
  workspaceId: string;
  table: 'program_scoped_content_rows' | 'program_fixed_content_rows';
  newRowId: string;
  learningText: string | null | undefined;
}): Promise<void> {
  const sid = params.workspaceId.trim();
  const newId = params.newRowId.trim();
  if (!sid || !newId) return;
  const forked = forkPresentationRefInLearningText(params.learningText);
  if (!forked) return;
  try {
    await copyProgramContentSlidePagesBetweenOwners(
      sid,
      { kind: 'presentation_ref', id: forked.fromRef },
      { kind: 'presentation_ref', id: forked.toRef }
    );
  } catch {
    try {
      const stripped = learningTextStorageWithoutPresentationRef(params.learningText);
      await supabase.from(params.table).update({ learning_text: stripped }).eq('id', newId);
    } catch {
      /* */
    }
    return;
  }
  try {
    const { error } = await supabase
      .from(params.table)
      .update({ learning_text: forked.nextLearningText })
      .eq('id', newId);
    if (error) throw new Error(error.message);
  } catch {
    /* learning_text 갱신 실패는 본 저장과 분리 */
  }
}

/** 수업 통합: 여러 presentation_ref 슬라이드를 하나의 target ref 아래로 이어 붙인 뒤 소스 ref 페이지는 삭제한다. */
export async function mergePresentationRefsIntoTarget(
  workspaceId: string,
  targetRefId: string,
  sourceRefIds: string[]
): Promise<void> {
  const sid = workspaceId.trim();
  const tid = targetRefId.trim();
  if (!sid || !isPersistableUuid(tid)) return;
  const sources = Array.from(
    new Set(sourceRefIds.map((id) => id.trim()).filter((id) => id && isPersistableUuid(id) && id !== tid))
  );
  if (!sources.length) return;
  let maxIdx = -1;
  const existingTarget = await fetchProgramContentSlidePages(sid, { kind: 'presentation_ref', id: tid });
  existingTarget.forEach((r) => {
    maxIdx = Math.max(maxIdx, r.page_index);
  });
  let nextIdx = maxIdx + 1;
  for (const srcId of sources) {
    const pages = await fetchProgramContentSlidePages(sid, { kind: 'presentation_ref', id: srcId });
    const sorted = [...pages].sort((a, b) => a.page_index - b.page_index);
    for (const p of sorted) {
      await upsertProgramContentSlidePage({
        workspaceId: sid,
        owner: { kind: 'presentation_ref', id: tid },
        pageIndex: nextIdx++,
        layout: layoutFromDbRow(p),
      });
    }
    await deleteProgramContentSlidePagesForOwner(sid, { kind: 'presentation_ref', id: srcId });
  }
  setPresenceCached(sid, { kind: 'presentation_ref', id: tid }, true);
}

export async function queueProgramContentSlideCopyDraft(params: {
  workspaceId: string;
  scopeId: string;
  from: ProgramContentSlideOwnerRef;
  to: ProgramContentSlideOwnerRef;
}): Promise<number> {
  const sid = params.workspaceId.trim();
  const scopeId = params.scopeId.trim();
  const fromId = params.from?.id?.trim();
  const toId = params.to?.id?.trim();
  if (!sid || !scopeId || !fromId || !toId) return 0;
  if (params.from.kind === params.to.kind && fromId === toId) return 0;

  const pendingSourceClone = readCloneIntent(sid, params.from);
  if (pendingSourceClone?.scopeId.trim()) {
    await materializePendingProgramContentSlideClone(sid, params.from);
  }

  const layoutsByPageIndex: Record<number, ProgramSlideLayoutV1> = {};
  const sourceDraft = readDraftRecord(sid, params.from);
  if (!sourceDraft?.deleteAll) {
    const pages = await fetchProgramContentSlidePages(sid, params.from);
    pages.forEach((p) => {
      layoutsByPageIndex[p.page_index] = layoutFromDbRow(p);
    });
  }
  if (sourceDraft && !sourceDraft.deleteAll) {
    Object.entries(sourceDraft.layoutsByPageIndex ?? {}).forEach(([idxRaw, layout]) => {
      const idx = Number(idxRaw);
      if (!Number.isFinite(idx) || idx < 0) return;
      layoutsByPageIndex[idx] = parseSlideLayoutJson(layout);
    });
  }

  const entries = Object.entries(layoutsByPageIndex).sort((a, b) => Number(a[0]) - Number(b[0]));
  if (!entries.length) {
    removeCloneIntent(sid, params.to);
    return 0;
  }
  entries.forEach(([idxRaw, layout]) => {
    const idx = Number(idxRaw);
    if (!Number.isFinite(idx) || idx < 0) return;
    queueProgramContentSlideDraftChange({
      workspaceId: sid,
      scopeId,
      owner: params.to,
      pageIndex: idx,
      layout,
    });
  });
  setPresenceCached(sid, params.to, true);
  removeCloneIntent(sid, params.to);
  return entries.length;
}

export async function materializePendingProgramContentSlideClone(
  workspaceId: string,
  owner: ProgramContentSlideOwnerRef
): Promise<Record<number, ProgramSlideLayoutV1>> {
  const sid = workspaceId.trim();
  const intent = readCloneIntent(sid, owner);
  if (!intent?.scopeId.trim()) return {};
  const copied = await queueProgramContentSlideCopyDraft({
    workspaceId: sid,
    scopeId: intent.scopeId,
    from: intent.from,
    to: intent.to,
  });
  removeCloneIntent(sid, owner);
  if (copied <= 0) return {};
  return getProgramContentSlideDraftLayouts(sid, intent.to);
}

export async function queueProgramContentSlideMoveDrafts(params: {
  workspaceId: string;
  scopeId: string;
  moves: Array<{ from: ProgramContentSlideOwnerRef; to: ProgramContentSlideOwnerRef }>;
}): Promise<number> {
  const sid = params.workspaceId.trim();
  const scopeId = params.scopeId.trim();
  if (!sid || !scopeId || !params.moves.length) return 0;

  const normalizedMoves = params.moves
    .map((move) => ({
      from: { kind: move.from.kind, id: move.from.id.trim() } as ProgramContentSlideOwnerRef,
      to: { kind: move.to.kind, id: move.to.id.trim() } as ProgramContentSlideOwnerRef,
    }))
    .filter((move) => move.from.id && move.to.id && programContentSlidePresenceKey(move.from) !== programContentSlidePresenceKey(move.to));
  if (!normalizedMoves.length) return 0;

  const snapshots = new Map<string, Record<number, ProgramSlideLayoutV1>>();
  for (const { from } of normalizedMoves) {
    const key = programContentSlidePresenceKey(from);
    if (snapshots.has(key)) continue;
    const layoutsByPageIndex: Record<number, ProgramSlideLayoutV1> = {};
    const sourceDraft = readDraftRecord(sid, from);
    if (!sourceDraft?.deleteAll) {
      const pages = await fetchProgramContentSlidePages(sid, from);
      pages.forEach((p) => {
        layoutsByPageIndex[p.page_index] = layoutFromDbRow(p);
      });
    }
    if (sourceDraft && !sourceDraft.deleteAll) {
      Object.entries(sourceDraft.layoutsByPageIndex ?? {}).forEach(([idxRaw, layout]) => {
        const idx = Number(idxRaw);
        if (!Number.isFinite(idx) || idx < 0) return;
        layoutsByPageIndex[idx] = parseSlideLayoutJson(layout);
      });
    }
    snapshots.set(key, layoutsByPageIndex);
  }

  const targetLayouts = new Map<string, { owner: ProgramContentSlideOwnerRef; layouts: Record<number, ProgramSlideLayoutV1> }>();
  const sourceOwners = new Map<string, ProgramContentSlideOwnerRef>();
  normalizedMoves.forEach(({ from, to }) => {
    const layouts = snapshots.get(programContentSlidePresenceKey(from)) ?? {};
    if (!Object.keys(layouts).length) return;
    targetLayouts.set(programContentSlidePresenceKey(to), { owner: to, layouts });
    sourceOwners.set(programContentSlidePresenceKey(from), from);
  });
  if (!targetLayouts.size) return 0;

  targetLayouts.forEach(({ owner, layouts }) => {
    writeDraftRecord({
      v: 1,
      workspaceId: sid,
      scopeId,
      owner,
      layoutsByPageIndex: Object.fromEntries(
        Object.entries(layouts).map(([idx, layout]) => [idx, parseSlideLayoutJson(layout)])
      ),
      deleteAll: true,
      updatedAt: Date.now(),
    });
    setPresenceCached(sid, owner, true);
  });
  sourceOwners.forEach((owner, key) => {
    if (targetLayouts.has(key)) return;
    queueProgramContentSlideDeleteDraft({ workspaceId: sid, scopeId, owner });
  });

  return targetLayouts.size;
}

/** 해당 owner에 연결된 슬라이드 페이지 전부 삭제(잘라내기 후 원본 비우기 등) */
export async function deleteProgramContentSlidePagesForOwner(
  workspaceId: string,
  owner: ProgramContentSlideOwnerRef
): Promise<void> {
  if (!workspaceId.trim() || !isPersistableUuid(owner.id)) return;
  const col =
    owner.kind === 'program_cell'
      ? 'program_progress_cell_id'
      : owner.kind === 'fixed_row'
        ? 'program_fixed_content_row_id'
        : owner.kind === 'presentation_ref'
          ? 'presentation_ref_id'
          : 'program_scoped_content_row_id';
  const { error } = await supabase
    .from('program_content_slide_pages')
    .delete()
    .eq('workspace_id', workspaceId.trim())
    .eq(col, owner.id.trim());
  if (error) throw new Error(error.message);
  removeProgramContentSlidePagesCache(workspaceId, owner);
  setPresenceCached(workspaceId, owner, false);
  removePresentationOwnerStatsCacheKeys(workspaceId.trim(), [programContentSlidePresenceKey(owner)]);
  invalidateAllPresentationAuditQueriesForWorkspace(workspaceId.trim());
}

/** 통합: survivor에 다른 행들의 슬라이드 페이지를 이어 붙인다(저장으로 피통합 행이 삭제되기 전에 호출). */
export async function mergeProgramContentSlidePagesIntoOwner(params: {
  workspaceId: string;
  target: ProgramContentSlideOwnerRef;
  sourceOwners: ProgramContentSlideOwnerRef[];
}): Promise<void> {
  const { workspaceId, target, sourceOwners } = params;
  let maxIdx = -1;
  const existing = await fetchProgramContentSlidePages(workspaceId, target);
  existing.forEach((r) => {
    maxIdx = Math.max(maxIdx, r.page_index);
  });
  let nextIdx = maxIdx + 1;
  for (const src of sourceOwners) {
    if (src.kind === target.kind && src.id === target.id) continue;
    const pages = await fetchProgramContentSlidePages(workspaceId, src);
    const sorted = [...pages].sort((a, b) => a.page_index - b.page_index);
    for (const p of sorted) {
      await upsertProgramContentSlidePage({
        workspaceId,
        owner: target,
        pageIndex: nextIdx++,
        layout: layoutFromDbRow(p),
      });
    }
  }
}

export function parseSlideCopyFromScopedRowId(row: { _slidePageCopyFromScopedRowId?: string }): string | undefined {
  const raw = (row._slidePageCopyFromScopedRowId ?? '').trim();
  return isPersistableUuid(raw) ? raw : undefined;
}

export function parseSlideCopyFromFixedRowId(row: { _slidePageCopyFromFixedRowId?: string }): string | undefined {
  const raw = (row._slidePageCopyFromFixedRowId ?? '').trim();
  return isPersistableUuid(raw) ? raw : undefined;
}
